/* eslint-disable react-refresh/only-export-components */
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
  Button,
} from "@a7y/tailwind-catalyst";
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

type ConfirmColor = "indigo" | "red" | "amber" | "green" | "zinc";

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: ConfirmColor;
}

export interface AlertOptions {
  title: string;
  description?: ReactNode;
  okLabel?: string;
}

type DialogState =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "alert"; options: AlertOptions; resolve: () => void }
  | null;

interface ConfirmDialogContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setState({ kind: "confirm", options, resolve });
      }),
    [],
  );

  const alert = useCallback(
    (options: AlertOptions) =>
      new Promise<void>((resolve) => {
        setState({ kind: "alert", options, resolve });
      }),
    [],
  );

  // Plain closures over `state`, not useCallback([]) reading a ref written
  // during render. These are only ever used as event handlers on the JSX below,
  // so nothing depends on their identity being stable.
  const handleClose = () => {
    if (!state) return;
    if (state.kind === "confirm") state.resolve(false);
    else state.resolve();
    setState(null);
  };

  const handleConfirm = () => {
    if (state?.kind !== "confirm") return;
    state.resolve(true);
    setState(null);
  };

  const handleAcknowledge = () => {
    if (state?.kind !== "alert") return;
    state.resolve();
    setState(null);
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm, alert }}>
      {children}
      {state?.kind === "confirm" && (
        <Alert open onClose={handleClose}>
          <AlertTitle>{state.options.title}</AlertTitle>
          {state.options.description !== undefined && (
            <AlertDescription>{state.options.description}</AlertDescription>
          )}
          <AlertActions>
            <Button plain onClick={handleClose}>
              {state.options.cancelLabel ?? "Cancel"}
            </Button>
            <Button color={state.options.confirmColor ?? "indigo"} onClick={handleConfirm}>
              {state.options.confirmLabel ?? "Confirm"}
            </Button>
          </AlertActions>
        </Alert>
      )}
      {state?.kind === "alert" && (
        <Alert open onClose={handleAcknowledge}>
          <AlertTitle>{state.options.title}</AlertTitle>
          {state.options.description !== undefined && (
            <AlertDescription>{state.options.description}</AlertDescription>
          )}
          <AlertActions>
            <Button color="indigo" onClick={handleAcknowledge}>
              {state.options.okLabel ?? "OK"}
            </Button>
          </AlertActions>
        </Alert>
      )}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmDialogProvider");
  return ctx.confirm;
}

export function useAlert() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) throw new Error("useAlert must be used inside ConfirmDialogProvider");
  return ctx.alert;
}
